// A meeting note an agent produced in the interaction note format (#12284):
// JSON whose note body is HTML. The body is agent-written, and what an agent
// reads can steer what it writes, so it renders in the sandboxed frame used
// for every HTML we did not write, never in the page. The frame can load
// nothing from the app, so it carries its own stylesheet.
import React, { useMemo } from "react";

import HtmlIframeView from "@/components/HtmlViewer/HtmlIframeView";

/** The note body a `.fp` file holds, or null when its text is not note JSON
 *  (an agent may write anything under that name). */
export const agentNoteBody = (text: string): string | null => {
  try {
    const data: unknown = JSON.parse(text);
    if (data !== null && typeof data === "object") {
      const body = (data as { content_html?: unknown }).content_html;
      if (typeof body === "string") return body;
    }
  } catch {
    // Not JSON: the caller shows it as text.
  }
  return null;
};

// The app's type and ink, restated for a document that cannot load the app's
// stylesheet or its web font: Figtree where the reader has it installed, the
// system sans-serif otherwise.
const NOTE_STYLE = `
  html { color-scheme: light; }
  body {
    margin: 0;
    padding: 20px 24px 32px;
    font-family: Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI",
      Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #10141B;
    background: #FFFFFF;
    overflow-wrap: anywhere;
  }
  h1, h2, h3, h4 { font-weight: 600; line-height: 1.3; margin: 20px 0 8px; }
  h1 { font-size: 22px; margin-top: 4px; }
  h2 { font-size: 17px; }
  h3 { font-size: 15px; }
  h4 { font-size: 14px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 0 0 12px; padding-left: 22px; }
  li { margin: 3px 0; }
  a { color: #2A3DCB; }
  strong { font-weight: 600; }
  hr { border: 0; border-top: 1px solid #ECEDEF; margin: 20px 0; }
  blockquote { margin: 0 0 12px; padding-left: 12px;
    border-left: 3px solid #ECEDEF; color: #6E7480; }
  table { border-collapse: collapse; margin: 0 0 12px; }
  th, td { border: 1px solid #ECEDEF; padding: 6px 10px; text-align: left; }
  th { background: #F7F7F8; font-weight: 600; }
  img { max-width: 100%; }
`;

/** The note body as a whole document, with the note's stylesheet in its
 *  head (the frame adds its content policy beside it). */
export const agentNoteDocument = (body: string): string =>
  `<!DOCTYPE html><html><head><meta charset="utf-8">` +
  `<style>${NOTE_STYLE}</style></head><body>${body}</body></html>`;

const AgentNoteView = ({ body, title }: { body: string; title: string }) => {
  const html = useMemo(() => agentNoteDocument(body), [body]);
  return <HtmlIframeView html={html} title={title} />;
};

export default AgentNoteView;
